/**
 * Resizable frame wrapper components for the shapes workshop page.
 *
 * Each wrapper binds useFrameResize to a specific frame shape component
 * so every instance on the page has its own independent size state.
 */

import { TextFrame } from "@/workshop/components/shapes/text-frame/text-frame";
import { CodeFrame } from "@/workshop/components/shapes/code-frame/code-frame";
import { SearchFrame } from "@/workshop/components/shapes/search-frame/search-frame";
import { ToolFrame } from "@/workshop/components/shapes/tool-frame/tool-frame";
import { WorkflowFrame } from "@/workshop/components/shapes/workflow-frame/workflow-frame";
import type { FlowStep } from "@/workshop/components/shapes/workflow-frame/types";
import { ImageFrame } from "@/workshop/components/shapes/image-frame/image-frame";
import { MemoryFrame } from "@/workshop/components/shapes/memory-frame/memory-frame";
import { DataFrame } from "@/workshop/components/shapes/data-frame/data-frame";
import { HtmlFrame } from "@/workshop/components/shapes/html-frame/html-frame";
import { ConnectionFrame } from "@/workshop/components/shapes/connection-frame/connection-frame";
import { ConnectAppFrame } from "@/workshop/components/shapes/connection-frame/connect-app-frame";
import type { AppId } from "@/workshop/components/shapes/connection-frame/app-icons";
import { useFrameResize } from "@/workshop/hooks/use-frame-resize";
import type { CSSProperties, ReactNode } from "react";

const noop = () => {};

function FramePreviewNode({
  children,
  testId,
  width,
}: {
  children: ReactNode;
  testId: string;
  width: number;
}) {
  return (
    <div
      className="react-flow__node"
      style={{ position: "relative", width } as CSSProperties}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

export function ResizableText(props: {
  title: string;
  text: string;
  status: "idle" | "streaming" | "error" | "done";
  agentName?: string;
  initialWidth?: number;
}) {
  const { width, onResizeStart } = useFrameResize({ initialWidth: props.initialWidth ?? 400 });
  return (
    <FramePreviewNode width={width} testId="resizable-text-frame">
      <TextFrame
        title={props.title}
        text={props.text}
        status={props.status}
        agentName={props.agentName}
        onCopy={noop}
        onResizeStart={onResizeStart}
      />
    </FramePreviewNode>
  );
}

export function ResizableCode(props: {
  title: string;
  code: string;
  language: string;
  status: "idle" | "streaming" | "error" | "done";
  agentName?: string;
  initialWidth?: number;
}) {
  const { width, onResizeStart } = useFrameResize({ initialWidth: props.initialWidth ?? 400 });
  return (
    <FramePreviewNode width={width} testId="resizable-code-frame">
      <CodeFrame
        title={props.title}
        code={props.code}
        language={props.language}
        status={props.status}
        agentName={props.agentName}
        onCopy={noop}
        onResizeStart={onResizeStart}
      />
    </FramePreviewNode>
  );
}

export function ResizableSearch(props: {
  query: string;
  results: { title: string; snippet: string; url: string; image?: string; isTopPick?: boolean }[];
  status: "idle" | "streaming" | "error" | "done";
  agentName?: string;
  initialWidth?: number;
}) {
  const { width, onResizeStart } = useFrameResize({ initialWidth: props.initialWidth ?? 400 });
  return (
    <FramePreviewNode width={width} testId="resizable-search-frame">
      <SearchFrame
        query={props.query}
        results={props.results}
        status={props.status}
        agentName={props.agentName}
        onCopy={noop}
        onResizeStart={onResizeStart}
      />
    </FramePreviewNode>
  );
}

export function ResizableTool(props: {
  title: string;
  toolName: string;
  input: string;
  output: string;
  status: "idle" | "streaming" | "error" | "done";
  agentName?: string;
  initialWidth?: number;
}) {
  const { width, onResizeStart } = useFrameResize({ initialWidth: props.initialWidth ?? 400 });
  return (
    <FramePreviewNode width={width} testId="resizable-tool-frame">
      <ToolFrame
        title={props.title}
        toolName={props.toolName}
        input={props.input}
        output={props.output}
        status={props.status}
        agentName={props.agentName}
        onCopy={noop}
        onResizeStart={onResizeStart}
      />
    </FramePreviewNode>
  );
}

export function ResizableWorkflow(props: {
  title: string;
  flowSteps: FlowStep[];
  status: "idle" | "streaming" | "error" | "done";
  agentName?: string;
  onApprove?: (stepId: string) => void;
  onReject?: (stepId: string) => void;
  initialWidth?: number;
}) {
  const { width, onResizeStart } = useFrameResize({ initialWidth: props.initialWidth ?? 400 });
  return (
    <FramePreviewNode width={width} testId="resizable-workflow-frame">
      <WorkflowFrame
        title={props.title}
        flowSteps={props.flowSteps}
        status={props.status}
        agentName={props.agentName}
        onApprove={props.onApprove}
        onReject={props.onReject}
        onResizeStart={onResizeStart}
      />
    </FramePreviewNode>
  );
}

export function ResizableImage(props: {
  title: string;
  images: { url: string; alt?: string; caption?: string }[];
  status: "idle" | "streaming" | "error" | "done";
  agentName?: string;
  initialWidth?: number;
}) {
  const { width, onResizeStart } = useFrameResize({ initialWidth: props.initialWidth ?? 400 });
  return (
    <FramePreviewNode width={width} testId="resizable-image-frame">
      <ImageFrame
        title={props.title}
        images={props.images}
        status={props.status}
        agentName={props.agentName}
        onResizeStart={onResizeStart}
      />
    </FramePreviewNode>
  );
}

export function ResizableMemory(props: {
  title?: string;
  agentName?: string;
  initialWidth?: number;
}) {
  const { width, onResizeStart } = useFrameResize({ initialWidth: props.initialWidth ?? 400 });
  return (
    <FramePreviewNode width={width} testId="resizable-memory-frame">
      <MemoryFrame
        title={props.title}
        agentName={props.agentName}
        onResizeStart={onResizeStart}
      />
    </FramePreviewNode>
  );
}

export function ResizableData(props: {
  title?: string;
  agentName?: string;
  initialWidth?: number;
}) {
  const { width, onResizeStart } = useFrameResize({ initialWidth: props.initialWidth ?? 400 });
  return (
    <FramePreviewNode width={width} testId="resizable-data-frame">
      <DataFrame
        title={props.title}
        agentName={props.agentName}
        onResizeStart={onResizeStart}
      />
    </FramePreviewNode>
  );
}

export function ResizableHtml({
  html,
  status,
  agentName,
  initialWidth,
}: {
  html: string;
  status: "idle" | "streaming" | "error" | "done";
  agentName?: string;
  initialWidth?: number;
}) {
  const { width, onResizeStart } = useFrameResize({ initialWidth: initialWidth ?? 400 });
  return (
    <FramePreviewNode width={width} testId="resizable-html-frame">
      <HtmlFrame
        title="Sales Dashboard"
        html={html}
        status={status}
        agentName={agentName}
        onCopy={noop}
        onResizeStart={onResizeStart}
      />
    </FramePreviewNode>
  );
}

export function ResizableConnection(props: {
  agentName?: string;
  initialWidth?: number;
}) {
  const { width, onResizeStart } = useFrameResize({ initialWidth: props.initialWidth ?? 400 });
  return (
    <FramePreviewNode width={width} testId="resizable-connection-frame">
      <ConnectionFrame
        agentName={props.agentName}
        onResizeStart={onResizeStart}
      />
    </FramePreviewNode>
  );
}

export function ResizableConnectApp(props: {
  appId?: AppId;
  reason?: string;
  agentName?: string;
  initialWidth?: number;
}) {
  const { width, onResizeStart } = useFrameResize({ initialWidth: props.initialWidth ?? 400 });
  return (
    <FramePreviewNode width={width} testId="resizable-connect-app-frame">
      <ConnectAppFrame
        appId={props.appId}
        reason={props.reason}
        agentName={props.agentName}
        onResizeStart={onResizeStart}
      />
    </FramePreviewNode>
  );
}
